import { GraviButton, GraviGrid, MaterialIcon, Texto, Vertical } from '@gravitate-js/excalibrr'
import type { ColDef, ColGroupDef, GridApi, IFilter, RowStyle } from 'ag-grid-community'
import { Card } from 'antd'
import type React from 'react'
import { type MutableRefObject, type Ref, useMemo } from 'react'

import type {
  InvoiceDetail,
  InvoiceGroup,
  InvoiceTransaction,
  TransactionDetail,
} from '../../../../../api/useFreight/types'
import type { TransactionRow } from '../../../../../api/useFreight/types'
import { getDetailLineItemColumns, rebillModeColumns } from '../../../shared/LineItems/detailLineItemColumns'
import { useSecurityContext } from '../../../../Navigation/SecurityContext'
import { FileAddOutlined } from '@ant-design/icons'
import { AddFreightLineItemPopover } from '@/components/Freight/FreightLineItems/components/AddFreightLineItem'

interface ExtendedInvoiceGroup extends InvoiceGroup {
  counterparty_id: string
  book_type: 'Cost' | 'Revenue'
}

type LineItemFilter = {
  type?: 'Base Freight' | 'Accessorial' | 'Surcharge'
}

interface DetailsLineItemsGridProps {
  filteredFreightLines: InvoiceDetail[] | TransactionDetail[]
  selectedOrder?: InvoiceTransaction | null
  freightLinesGridRef: MutableRefObject<GridApi<InvoiceDetail>>
  hideActions?: boolean
  hideStatus?: boolean
  rebillUpdateActions?: {
    overrideRebillLineItem?: (detail: InvoiceDetail, amountOverride: number, totalOverride: number) => void
    addRebillLineItem?: (detail: Omit<InvoiceDetail | TransactionDetail, 'id'>, orderId: string) => void
  }
  isRebillMode?: boolean
  showAddAccessorial?: boolean
  singleOrder: TransactionRow | InvoiceTransaction
  invoiceGroupDetails?: ExtendedInvoiceGroup
  key?: string
  //@ts-ignore
  filters?: Record<string, unknown>
  onFilterRemoved?: () => void
}

const StandardLineItemsGrid = ({
  filteredFreightLines,
  selectedOrder,
  freightLinesGridRef,
  hideActions,
  hideStatus,
  showAddAccessorial,
  singleOrder,
  invoiceGroupDetails,
  filters,
  onFilterRemoved,
}: Omit<DetailsLineItemsGridProps, 'isRebillMode' | 'rebillUpdateActions'>) => {
  const columnDefs = useMemo(
    () => getDetailLineItemColumns(hideActions, !!singleOrder, hideStatus),
    [hideActions, singleOrder, hideStatus]
  )
  const { scopes } = useSecurityContext()
  return (
    <GraviGrid<InvoiceDetail | TransactionDetail, Record<string, unknown>>
      primaryKey='id'
      externalRef={freightLinesGridRef}
      rowData={filteredFreightLines}
      controlBarProps={{
        title: selectedOrder ? `Freight Line Items - Order ${selectedOrder.order_number}` : 'Freight Line Items',
        actionButtons: showAddAccessorial ? (
          <AddFreightLineItemPopover
            actionType='create'
            order={singleOrder as TransactionRow}
            gridApi={freightLinesGridRef?.current}
            context={{
              counterpartyId: invoiceGroupDetails?.counterparty_id,
              bookType: invoiceGroupDetails?.book_type,
            }}
            isCreditRebillMode={false}
          >
            <GraviButton buttonText='Add Freight Line Item' theme2 icon={<FileAddOutlined />} />
          </AddFreightLineItemPopover>
        ) : undefined,
        filters,
        setFilters: onFilterRemoved ? () => onFilterRemoved() : undefined,
      }}
      agPropOverrides={{
        columnDefs: columnDefs as
          | ColDef<InvoiceDetail | TransactionDetail>[]
          | ColGroupDef<InvoiceDetail | TransactionDetail>[],
        getRowId: (params) => params.data?.id,
        rowGroupPanelShow: 'onlyWhenGrouping',
        getRowHeight: (params) => {
          const hasOverride =
            !!params.data?.total_override ||
            !!params.data?.amount_override ||
            !!params.data?.credit_rebill_metadata?.impact_type
          return hasOverride ? 40 : 32
        },
        context: {
          scopes,
          hideActions: hideActions,
        },
      }}
    />
  )
}

const RebillLineItemsGrid = ({
  filteredFreightLines,
  selectedOrder,
  freightLinesGridRef,
  hideActions,
  rebillUpdateActions,
  showAddAccessorial,
  singleOrder,
  invoiceGroupDetails,
  hideStatus,
  filters,
  onFilterRemoved,
}: Omit<DetailsLineItemsGridProps, 'isRebillMode'>) => {
  const columnDefs = useMemo(
    () => rebillModeColumns(hideActions, !!singleOrder, hideStatus),
    [hideActions, singleOrder, hideStatus]
  )
  const { scopes } = useSecurityContext()

  return (
    <GraviGrid<InvoiceDetail | TransactionDetail, IFilter>
      primaryKey='id'
      externalRef={freightLinesGridRef}
      rowData={filteredFreightLines}
      columnDefs={columnDefs as ColDef<InvoiceDetail | TransactionDetail>[]}
      controlBarProps={{
        title: selectedOrder ? `Freight Line Items - Order ${selectedOrder.order_number}` : 'Freight Line Items',
        actionButtons: showAddAccessorial ? (
          <AddFreightLineItemPopover
            actionType='create'
            order={singleOrder as TransactionRow}
            gridApi={freightLinesGridRef?.current}
            context={{
              rebillUpdateActions: rebillUpdateActions as {
                addRebillLineItem: (detail: Omit<InvoiceDetail | TransactionDetail, 'id'>, orderId: string) => void
              },
              counterpartyId: invoiceGroupDetails?.counterparty_id,
              bookType: invoiceGroupDetails?.book_type,
            }}
            isCreditRebillMode={true}
          >
            <GraviButton buttonText='Add Freight Line Item' theme2 icon={<FileAddOutlined />} />
          </AddFreightLineItemPopover>
        ) : undefined,
        //@ts-ignore
        filters,
        setFilters: onFilterRemoved ? () => onFilterRemoved() : undefined,
      }}
      agPropOverrides={{
        columnDefs: columnDefs as
          | ColDef<InvoiceDetail | TransactionDetail>[]
          | ColGroupDef<InvoiceDetail | TransactionDetail>[],
        getRowId: (params) => params.data?.id,
        rowGroupPanelShow: 'onlyWhenGrouping',
        getRowHeight: (params) => {
          // Rebill height logic
          if (params.data?.credit_rebill_metadata?.impact_type || params.data?.has_override) {
            return 40
          }
          return 32
        },
        context: {
          scopes,
          rebillUpdateActions: rebillUpdateActions, // Pass rebill actions
          hideActions: hideActions,
        },
        getRowStyle: (params): RowStyle | undefined => {
          // Rebill style logic
          const status = params.data?.credit_rebill_metadata?.impact_type
          if (status === 'added') {
            return { backgroundColor: 'var(--theme-success-dim)' }
          }
          if (status === 'removed') {
            return { backgroundColor: 'var(--theme-error-dim)' }
          }
          if (status === 'impacted') {
            return { backgroundColor: 'var(--theme-warning-dim)' }
          }
          if (params.data?.amount_override !== null || params.data?.total_override !== null) {
            return { backgroundColor: 'var(--theme-warning-dim)' }
          }
          return undefined
        },
      }}
    />
  )
}

export function DetailsLineItemsGrid({
  filteredFreightLines,
  selectedOrder,
  freightLinesGridRef,
  hideActions,
  hideStatus,
  rebillUpdateActions,
  isRebillMode,
  showAddAccessorial,
  singleOrder,
  invoiceGroupDetails,
  filters,
  onFilterRemoved,
}: DetailsLineItemsGridProps) {
  return (
    <Vertical flex={3} style={{ overflow: 'hidden' }}>
      {filteredFreightLines.length === 0 ? (
        <EmptyGrid selectedOrder={selectedOrder} />
      ) : isRebillMode ? (
        <RebillLineItemsGrid
          filteredFreightLines={filteredFreightLines}
          selectedOrder={selectedOrder}
          freightLinesGridRef={freightLinesGridRef}
          hideActions={hideActions}
          rebillUpdateActions={rebillUpdateActions}
          showAddAccessorial={showAddAccessorial}
          singleOrder={singleOrder}
          invoiceGroupDetails={invoiceGroupDetails}
          hideStatus={hideStatus}
          filters={filters}
          onFilterRemoved={onFilterRemoved}
        />
      ) : (
        <StandardLineItemsGrid
          filteredFreightLines={filteredFreightLines}
          selectedOrder={selectedOrder}
          freightLinesGridRef={freightLinesGridRef}
          hideActions={hideActions}
          hideStatus={hideStatus}
          showAddAccessorial={showAddAccessorial}
          singleOrder={singleOrder}
          invoiceGroupDetails={invoiceGroupDetails}
          filters={filters}
          onFilterRemoved={onFilterRemoved}
        />
      )}
    </Vertical>
  )
}

function EmptyGrid({
  selectedOrder,
}: {
  selectedOrder?: InvoiceTransaction | null
}) {
  return (
    <Card
      style={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Vertical horizontalCenter verticalCenter>
        <MaterialIcon icon='receipt_long' style={{ fontSize: '48px', color: 'var(--theme-color-1)' }} />
        <Texto category='h4' style={{ marginTop: '16px' }}>
          No Line Items Found
        </Texto>
        <Texto appearance='light' style={{ textAlign: 'center', maxWidth: '300px', marginTop: '8px' }}>
          {selectedOrder
            ? `There are no line items associated with order ${selectedOrder.order_number}.`
            : 'There are no line items associated with this invoice group yet.'}
        </Texto>
      </Vertical>
    </Card>
  )
}
